import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface AddRuleDialogData {
  apiUrl?: string;
  entity?: string;
  batchId?: string;
  workflowId?: string;
  version?: string;
}

@Component({
  selector: 'app-add-rule-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './add-rule-dialog.component.html',
  styleUrl: './add-rule-dialog.component.scss'
})
export class AddRuleDialogComponent {
  form: FormGroup;
  isSubmitting = false;
  submitSuccess = false;
  submitError = '';

  readonly applyLevelOptions = ['Line', 'Header', 'Global'];
  readonly stackableOptions = ['Yes', 'No'];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    public dialogRef: MatDialogRef<AddRuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddRuleDialogData
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      max_discount_amount: ['', Validators.required],
      discount_percent: ['', Validators.required],
      apply_level: ['Line', Validators.required],
      priority: ['', Validators.required],
      stackable: ['yes', Validators.required],
      exclusive_group: [null],
      criteria_field: ['', Validators.required],
      criteria_value: ['', Validators.required],
      organisation_id: [null]
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.submitError = '';
    this.submitSuccess = false;

    const formValues = this.form.getRawValue();

    const payload = {
      requestData: {
        name: formValues.name,
        max_discount_amount: formValues.max_discount_amount,
        discount_percent: formValues.discount_percent,
        apply_level: formValues.apply_level,
        priority: formValues.priority,
        stackable: formValues.stackable,
        exclusive_group: formValues.exclusive_group || null,
        criteria_field: formValues.criteria_field,
        criteria_value: formValues.criteria_value,
        organisation_id: formValues.organisation_id || null,
        operation: 'insert'
      },
      responseList: [],
      entity: this.data?.entity || 'discount_rules',
      batchId: this.data?.batchId || 'test',
      workflowId: this.data?.workflowId || 'ABC-123',
      endpoints: [],
      version: this.data?.version || '1.0'
    };

    const apiUrl = this.data?.apiUrl || '/spriced/platform';

    this.http.post(apiUrl, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.submitSuccess = true;
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.submitError =
          err?.error?.message || err?.message || 'Failed to save rule. Please try again.';
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
